export type TopicTypeId =
  | 'beautification'
  | 'challenge'
  | 'deal'
  | 'event'
  | 'rally'
  | 'news'
  | 'volunteering';

export interface TopicType {
  id: TopicTypeId;
  choiceLabel: string;
  messageSubstitution: string;
  description: string;
  /**
   * Whether a share of this type opens with "Exclude recipients who've seen
   * this topic before" already ticked (#581). A type that spreads by reaching
   * people who have not seen it yet wants exclusion as its opening position;
   * the rest open unticked, where prior awareness routes someone to a reminder
   * rather than dropping them. The sender can change it either way, and the
   * update flow ignores it — that flow exists to reach people who already know.
   */
  defaultsToExcludingAwareRecipients?: boolean;
  /**
   * How the reshare story describes the starting sender of a topic of this
   * type that has not picked a description in its Settings tab ("Sally, the
   * author"). A type that says nothing reads as
   * DEFAULT_STARTING_SENDER_APPOSITIVE. Mirrors
   * `topic.DefaultStartingSenderAppositive` on the backend, which tells a
   * stored topic's stories with the same default.
   */
  startingSenderAppositive?: string;
  /**
   * Whether a topic of this type has no end time. A news topic is not an
   * event: its start time dates the story and it never ends. Types that say
   * nothing have both a start and an end.
   */
  hasNoEndTime?: boolean;
}

export const TOPIC_TYPES: readonly TopicType[] = [
  {
    id: 'beautification',
    choiceLabel: 'Beautification',
    messageSubstitution: 'beautification',
    description: 'Neighborhood clean up, trail maintenance, lake clean up',
  },
  {
    id: 'challenge',
    choiceLabel: 'Challenge',
    messageSubstitution: 'challenge',
    description: 'Screen-free Sunday, random acts of kindness, healthy habits',
  },
  {
    id: 'deal',
    choiceLabel: 'Deal',
    messageSubstitution: 'deal',
    description: 'Yard sale, bake sale, sidewalk sale, free curb pickup',
  },
  {
    id: 'event',
    choiceLabel: 'Event',
    messageSubstitution: 'event',
    description: 'When your topic has a specific date/time',
  },
  {
    id: 'rally',
    choiceLabel: 'Rally',
    messageSubstitution: 'rally',
    description: 'March or gathering in support of a cause',
  },
  {
    id: 'news',
    choiceLabel: 'News',
    messageSubstitution: 'news',
    description:
      'A quote passed person to person through a chain of people who trust each other',
    defaultsToExcludingAwareRecipients: true,
    startingSenderAppositive: 'the author',
    hasNoEndTime: true,
  },
  {
    id: 'volunteering',
    choiceLabel: 'Volunteering',
    messageSubstitution: 'volunteering',
    description: 'Soup kitchen, health clinic, elderly tech support',
  },
];

/**
 * Topic data structure from API responses and internal use.
 */
export interface Topic {
  id: string;
  title: string;
  body: string;
  imageData?: string;
  thumbnailImageData?: string;
  startTime: string;
  /** Absent for a news topic, which is not an event and has no end. */
  endTime?: string;
  timezone: string;
  createdAt: string;
  updatedAt: string;
  introCountLimit?: number | null;
  onlyManagersCanShare?: boolean;
  introCount?: number;
  /**
   * Email addresses of this topic's listing managers, returned by the topic
   * detail endpoint (not the list endpoint). Powers the "Listing managers"
   * contact card on the topic view.
   */
  managerEmails?: string[];
  type: TopicTypeId;
  byInviteOnly?: boolean;
  /**
   * Optional manager-authored override of the standard message templates for
   * this topic, stored as the full templates YAML. When present it is used for
   * the topic's messages instead of the standard per-type templates.
   */
  customTemplates?: string;
  /**
   * How many people this topic aims to reach (#447). Any topic type may set
   * one; absent or null means no goal. Progress is measured against the
   * topic's total impressions and reaching it never blocks a share.
   */
  impressionGoal?: number | null;
  /**
   * Type-specific payload (#447), stored opaquely. Mirrors topics.props on the
   * backend.
   */
  props?: TopicProps | null;
  /**
   * The sharing setup's Settings tab: whether a reshare carries each sender's
   * allowed detail, and the sentence that frames it. Absent or null for a topic
   * that has never saved the tab, which reads as not including a detail.
   */
  reshareStorySettings?: ReshareStorySettings | null;
  /**
   * The name a news topic's message credits ("By JOHN DOE"), stored as it is
   * shown. Absent leaves the byline out.
   */
  byline?: string;
  /**
   * A short place, typically a city or a meeting point: a news topic's
   * dateline place, or where the other types meet. Absent leaves it out.
   */
  location?: string;
}

/** Type-specific topic payload (#447); see Topic.props. */
export interface TopicProps {
  /** Example field values for listing display, stored opaquely. */
  exampleFieldValues?: Record<string, string>;
}

/**
 * How a reshared message tells where it came from; see
 * Topic.reshareStorySettings. Mirrors `topic.ReshareStorySettings` on the
 * backend, which accepts these keys only.
 */
export interface ReshareStorySettings {
  /**
   * Present only while the tab names a slot to take the allowed detail from;
   * absent, a reshare carries no detail.
   */
  allowedDetailSettings?: AllowedDetailSettings;
  /**
   * Who the reshare story tells about beyond naming the sender and the
   * starting sender: their allowed details, and with `between`, everyone in
   * between by name. Absent reads as DEFAULT_STORY_PARTICIPANTS; an empty list
   * tells about none.
   */
  participants?: StoryParticipant[];
  /**
   * Describes the first sender after their name ("AR, who got the ball
   * rolling"). Absent reads as the topic type's default
   * (TopicLogic.defaultStartingSenderAppositive); an empty string means none,
   * and their detail follows their name instead.
   */
  startingSenderAppositive?: string;
  /**
   * Whether a topic of this type has no end time. A news topic is not an
   * event: its start time dates the story and it never ends. Types that say
   * nothing have both a start and an end.
   */
  hasNoEndTime?: boolean;
}

/**
 * How a reshared message carries each sender's allowed detail. Mirrors
 * `topic.AllowedDetailSettings` on the backend.
 *
 * The detail is the sender's answer to one answerable slot of the intro form,
 * named by its stable field id `slotId`. The leg carries only that answer
 * ("mirror"); `prefix` and `suffix` frame it as a first person sentence when
 * the story is told ("I would bring a" + "mirror" + "to the island").
 */
export interface AllowedDetailSettings {
  prefix?: string;
  slotId: string;
  suffix?: string;
}

/**
 * The participants a reshare story can tell about: whoever sent the message,
 * whoever started the run, and everyone in between.
 */
export const STORY_PARTICIPANT_CHOICES = [
  'sender',
  'startingSender',
  'between',
] as const;
export type StoryParticipant = (typeof STORY_PARTICIPANT_CHOICES)[number];

export const DEFAULT_STORY_PARTICIPANTS: readonly StoryParticipant[] = [
  'sender',
  'startingSender',
];
export const DEFAULT_STARTING_SENDER_APPOSITIVE = 'who got the ball rolling';

/**
 * Which name a news story's byline uses: one of the author's attribution names
 * (kept only on their device until the byline is saved), a custom name typed
 * in, or none.
 */
export type BylineChoice = 'familiar' | 'allowed' | 'custom' | 'none';

/** The author's attribution names a byline choice resolves against. */
export interface BylineNames {
  familiarName?: string;
  allowedName?: string;
}

export interface Registration {
  id: string;
  userId: string;
  topicId: string;
  registeredAt: string;
  attendanceLikelihood: number | null; // 0.0–1.0 probability
  smsReminder: boolean;
}

/** A core field `TopicLogic.computeCoreDiffs` reports a before/after for. */
export type TopicDiffFieldKey =
  | 'title'
  | 'type'
  | 'datetime'
  | 'location'
  | 'body';

export interface TopicFieldDiff {
  key: TopicDiffFieldKey;
  label: string;
  before: string;
  after: string;
}

/** The core changes between two versions of a topic. */
export interface TopicDiffSet {
  /** Changed scalar fields, in display order. Never empty when present. */
  fields: TopicFieldDiff[];
  /** The representative image was added, removed, or replaced. */
  imageChanged: boolean;
}

/**
 * A saved setting `TopicLogic.computeOtherDiffs` compares: one recipients
 * never see as a change to the topic itself.
 */
export type TopicOtherDiffKey =
  | 'introCountLimit'
  | 'onlyManagersCanShare'
  | 'byInviteOnly'
  | 'customTemplates'
  | 'reshareStorySettings'
  | 'impressionGoal'
  | 'byline';
